import { supabase } from "../supabaseClient";

export const PLATFORM_SETTINGS_ROW_ID = 1;
export const PLATFORM_SETTINGS_UPDATED_EVENT = "echo_jham_platform_settings_updated";

export const DEFAULT_PLATFORM_SETTINGS = {
  organizationName: "Echo Jham",
  organizationEmail: "support@echojham.com",
  organizationPhone: "+63 (0) 2 1234 5678",
  organizationWebsite: "https://echojham.com",
  facebookUrl: "",
  instagramUrl: "",
  maintenanceMode: false,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const trimText = (value) => String(value ?? "").trim();

const httpUrlError = (value, emptyMessage) => {
  if (!value) return "";
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return emptyMessage;
    }
  } catch (error) {
    return emptyMessage;
  }
  return "";
};

export const normalizePlatformSettings = (row = {}) => {
  const organizationName = trimText(row.organizationName ?? row.organization_name);
  const organizationEmail = trimText(row.organizationEmail ?? row.organization_email);
  const organizationPhone = trimText(row.organizationPhone ?? row.organization_phone);
  const organizationWebsite = trimText(row.organizationWebsite ?? row.organization_website);
  const facebookUrl = trimText(row.facebookUrl ?? row.facebook_url);
  const instagramUrl = trimText(row.instagramUrl ?? row.instagram_url);
  const maintenanceRaw = row.maintenanceMode ?? row.maintenance_mode;

  return {
    organizationName: organizationName || DEFAULT_PLATFORM_SETTINGS.organizationName,
    organizationEmail,
    organizationPhone,
    organizationWebsite,
    facebookUrl,
    instagramUrl,
    maintenanceMode: Boolean(maintenanceRaw),
  };
};

export const validatePlatformSettings = (settings) => {
  const normalized = normalizePlatformSettings(settings);
  const errors = {};

  if (!trimText(settings?.organizationName ?? settings?.organization_name)) {
    errors.organizationName = "Organization name is required.";
  }

  if (normalized.organizationEmail && !EMAIL_PATTERN.test(normalized.organizationEmail)) {
    errors.organizationEmail = "Enter a valid email address.";
  }

  const websiteError = httpUrlError(normalized.organizationWebsite, "Enter a valid website URL.");
  if (websiteError) errors.organizationWebsite = websiteError;

  const facebookError = httpUrlError(normalized.facebookUrl, "Facebook URL must start with http:// or https://.");
  if (facebookError) errors.facebookUrl = facebookError;

  const instagramError = httpUrlError(normalized.instagramUrl, "Instagram URL must start with http:// or https://.");
  if (instagramError) errors.instagramUrl = instagramError;

  return errors;
};

const toRowPayload = (settings, userId) => {
  const normalized = normalizePlatformSettings(settings);
  return {
    id: PLATFORM_SETTINGS_ROW_ID,
    organization_name: normalized.organizationName,
    organization_email: normalized.organizationEmail,
    organization_phone: normalized.organizationPhone,
    organization_website: normalized.organizationWebsite,
    facebook_url: normalized.facebookUrl,
    instagram_url: normalized.instagramUrl,
    maintenance_mode: normalized.maintenanceMode,
    updated_by: userId || null,
  };
};

const notifySettingsUpdated = (settings) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PLATFORM_SETTINGS_UPDATED_EVENT, { detail: settings }));
};

let cachedSettings = null;

export const getCachedPlatformSettings = () => cachedSettings;

export const loadPlatformSettings = async () => {
  const { data, error } = await supabase
    .from("platform_settings")
    .select(
      "id, organization_name, organization_email, organization_phone, organization_website, facebook_url, instagram_url, maintenance_mode"
    )
    .eq("id", PLATFORM_SETTINGS_ROW_ID)
    .maybeSingle();

  if (error) {
    throw error;
  }

  const settings = data ? normalizePlatformSettings(data) : { ...DEFAULT_PLATFORM_SETTINGS };
  cachedSettings = settings;
  return settings;
};

export const savePlatformSettings = async (settings, userId) => {
  const errors = validatePlatformSettings(settings);
  if (Object.keys(errors).length > 0) {
    const firstError = Object.values(errors)[0];
    const validationError = new Error(firstError);
    validationError.fieldErrors = errors;
    throw validationError;
  }

  const payload = toRowPayload(settings, userId);
  const { data, error } = await supabase
    .from("platform_settings")
    .upsert(payload, { onConflict: "id" })
    .select(
      "id, organization_name, organization_email, organization_phone, organization_website, facebook_url, instagram_url, maintenance_mode"
    )
    .single();

  if (error) {
    throw error;
  }

  const saved = normalizePlatformSettings(data);
  cachedSettings = saved;
  notifySettingsUpdated(saved);
  return saved;
};
